import { Lock, Key, Link } from 'lucide-react'
import SecureInput from '../../../ui/SecureInput/SecureInput'
import { CreateIdpProviderDto } from '../../../types/types'
import styles from '../SSOConfiguration.module.css'

interface AzureProviderFormProps {
  providerForm: CreateIdpProviderDto
  onFieldChange: (field: keyof CreateIdpProviderDto, value: string) => void
  onConfigChange: (key: string, value: string) => void
  hasClientSecret?: boolean
}

export default function AzureProviderForm({
  providerForm,
  onFieldChange,
  onConfigChange,
  hasClientSecret
}: AzureProviderFormProps) {
  return (
    <div className={styles.providerFormGrid}>
      <div className={styles.formField}>
        <SecureInput
          fieldType="name"
          label="Name *"
          value={providerForm.name}
          onChange={(value) => onFieldChange('name', value)}
          placeholder="e.g. Azure Production"
          className={styles.input}
        />
      </div>

      <div className={styles.formField}>
        <SecureInput
          fieldType="text"
          label="Description"
          value={providerForm.description || ''}
          onChange={(value) => onFieldChange('description', value)}
          placeholder="Optional description"
          className={styles.input}
        />
      </div>

      <div className={styles.formField}>
        <SecureInput
          fieldType="key"
          label="Azure Tenant ID *"
          icon={<Key size={16} />}
          value={providerForm.config?.tenantId || ''}
          onChange={(value) => onConfigChange('tenantId', value)}
          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
          className={styles.input}
          description="Azure AD Tenant ID (GUID)"
        />
      </div>

      <div className={styles.formField}>
        <SecureInput
          fieldType="key"
          label="Application (Client) ID *"
          icon={<Key size={16} />}
          value={providerForm.clientId || ''}
          onChange={(value) => onFieldChange('clientId', value)}
          placeholder="Application (client) ID from Azure"
          className={styles.input}
          description="Azure AD Application ID"
        />
      </div>

      <div className={styles.formField}>
        <SecureInput
          fieldType="password"
          label="Client Secret *"
          icon={<Lock size={16} />}
          value={providerForm.clientSecret || ''}
          onChange={(value) => onFieldChange('clientSecret', value)}
          placeholder={hasClientSecret ? 'Secret is set — leave blank to keep it' : 'Client secret value from Azure'}
          className={styles.input}
          skipPasswordValidation
          description={hasClientSecret
            ? 'A secret is already configured. Leave blank to keep it, or enter a new value to replace it.'
            : 'Azure AD Application client secret'}
          autoComplete="new-password"
        />
      </div>

      <div className={styles.formField}>
        <SecureInput
          fieldType="url"
          label="Issuer URL"
          icon={<Link size={16} />}
          value={providerForm.issuerUrl || ''}
          onChange={(value) => onFieldChange('issuerUrl', value)}
          placeholder="https://login.microsoftonline.com/{tenant-id}/v2.0"
          className={styles.input}
        />
      </div>

      <div className={styles.formField}>
        <SecureInput
          fieldType="text"
          label="Authorization URL"
          value={providerForm.authorizationUrl || ''}
          onChange={(value) => onFieldChange('authorizationUrl', value)}
          placeholder="/oauth2/v2.0/authorize"
          className={styles.input}
        />
      </div>

      <div className={styles.formField}>
        <SecureInput
          fieldType="text"
          label="Token URL"
          value={providerForm.tokenUrl || ''}
          onChange={(value) => onFieldChange('tokenUrl', value)}
          placeholder="/oauth2/v2.0/token"
          className={styles.input}
        />
      </div>

      <div className={styles.formField}>
        <SecureInput
          fieldType="text"
          label="Scopes"
          value={providerForm.config?.scope || ''}
          onChange={(value) => onConfigChange('scope', value)}
          placeholder="openid email profile User.Read"
          className={styles.input}
          description="Microsoft Graph scopes to request (space-separated)"
          autoComplete="off"
        />
      </div>

      <div className={styles.formField}>
        <label className={styles.fieldLabel}>Include Groups</label>
        <select
          value={providerForm.config?.includeGroups || 'false'}
          onChange={(e) => onConfigChange('includeGroups', e.target.value)}
          className={styles.select}
        >
          <option value="false">No</option>
          <option value="true">Yes</option>
        </select>
        {/* <p className={styles.fieldDescription}>Whether to fetch user group memberships from Microsoft Graph</p> */}
      </div>
    </div>
  )
}
